import {FunctionComponent, useCallback, useEffect, useMemo, useState} from "react";
import {MapboxAddress, MapboxCoordinates} from "../index";
import MapboxMarkers, {MapboxMarker} from "../markers";
import Utils from "../../core/services/utils";
import {Map, MapMouseEvent} from "mapbox-gl";


export interface MapInteractionsProps {
    map?: Map,
    onCoordinatesSelect?: (coordinates: MapboxCoordinates) => void;
    onAddressSelect?: (address: MapboxAddress) => void;
    fetchAddressFromCoordinates: (lng: number, lat: number) => Promise<MapboxAddress | undefined>;
    awaitMapLoad: (map: Map) => Promise<void>;
    markerColor?: string;
    flyToMarkerOnClick?: boolean;
}


const MapboxInteractions: FunctionComponent<MapInteractionsProps> = (props) => {
    const {onCoordinatesSelect, onAddressSelect, fetchAddressFromCoordinates, awaitMapLoad} = props;
    const [markers, setMarkers] = useState<MapboxMarker[]>([]);
    const interactionMarkerId = useMemo(() => Utils.createUUId(), []);

    /**
     * Retrieves the coordinates based on the location of the marker on the map and adds a new marker to the markers
     * of the mapbox
     *
     * If the onAddressSelect callback exists, fetches the address of the clicked marker and sends it to the callback
     */
    const onMapClicked = useCallback(async (e: MapMouseEvent) => {
        if (!props.map)
            return;
        await awaitMapLoad(props.map);
        setMarkers([{
            id: interactionMarkerId,
            center: {lng: e.lngLat.lng, lat: e.lngLat.lat},
            color: props.markerColor,
            flyToOnClick: props.flyToMarkerOnClick
        }]);
        if (onCoordinatesSelect) {
            void onCoordinatesSelect(e.lngLat);
        }
        if (onAddressSelect) {
            const mapboxAddress = await fetchAddressFromCoordinates(e.lngLat.lng, e.lngLat.lat);
            if (!!mapboxAddress) {
                void onAddressSelect(mapboxAddress);
            }
        }
    }, [props.map, props.markerColor, props.flyToMarkerOnClick, awaitMapLoad, interactionMarkerId, onCoordinatesSelect, onAddressSelect, fetchAddressFromCoordinates])

    /**
     * Adds a click event listener to the map that listens for clicks on the map.
     * - If the onAddressSelect callback exists, fetches the address of the clicked marker and sends it to the callback and creates a marker on the map
     */
    useEffect(() => {
        if (!props.map) {
            return;
        }
        if (!onAddressSelect && !onCoordinatesSelect) {
            return;
        }
        const map = props.map;
        map.on('click', onMapClicked);
        return () => {
            map.off('click', onMapClicked)
        };
    }, [props.map, onAddressSelect, onCoordinatesSelect, onMapClicked])

    return (
        <>
            <MapboxMarkers
                map={props.map}
                markers={markers}
                markerColor={props.markerColor}
                flyToMarkerOnClick={props.flyToMarkerOnClick}
            />
        </>
    );
}

export default MapboxInteractions;